import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMount, useUnmount } from "react-use";
import { ColDef, GridOptions, RowDoubleClickedEvent } from "@ag-grid-community/core";
import { Alert, AlertTitle, Box, Button } from "@mui/material";
import { Feature } from "geojson";
import { FeatureIdentifier } from "mapbox-gl";

import DrawHandler from "fond/draw/DrawHandler";
import { NBN_SOURCE, NBN_SOURCE_LAYER, NBN_STYLE_ID } from "fond/draw/modes/nbn/constants";
import nbnPassingDrawMode from "fond/draw/modes/nbn/nbnPassingDrawMode";
import nbnSelect from "fond/draw/modes/nbn/nbnSelect";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { MapContext } from "fond/map/MapProvider";
import { zoomTo } from "fond/map/redux";
import { PolygonSelectButton, SelectButton, ToolbarConfig } from "fond/map/Toolbar";
import { EditMode, selectFeature, setEditMode } from "fond/project/redux";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { AgGrid, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import nbnDrawStyles from "../IDDLite/nbnDrawStyles";

import PrioritizeButton from "./PrioritizeButton";

interface ExcludeDrawPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const config: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="polygonSelect" />],
  },
  {
    id: "excludeTools",
    type: "mode",
    items: [<PrioritizeButton key="prioritize" prioritize />, <PrioritizeButton key="defer" prioritize={false} />],
  },
];

const modes = {
  simple_select: nbnSelect,
  passing_draw_polygon: nbnPassingDrawMode,
};

const sources: FeatureIdentifier[] = [{ source: NBN_SOURCE, sourceLayer: NBN_SOURCE_LAYER }];

const drawHandlerOptions = {
  // Allow the drawHandler to select features from the NBN_STYLE_ID layer
  selectableLayerIds: [NBN_STYLE_ID],
};

const ExcludeDrawPanel: React.FC<ExcludeDrawPanelProps> = ({ isReadOnly }) => {
  const dispatch = useAppDispatch();
  const { drawControl, map, setDrawMode } = useContext(MapContext);
  const { goBack } = useStackedNavigationContext();
  const [rowData, setRowData] = useState<Feature[]>([]);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { runWorkflow } = useCustomWorkflow({ versionId, key: "nbn_cost_to_serve_iteration" });

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  useUnmount(() => {
    dispatch(setEditMode(EditMode.none));
  });

  const columns: ColDef[] = useMemo(
    () =>
      [
        {
          headerName: "Equipment ID",
          field: "properties.equipment_id",
          flex: 1,
        },
      ] as ColDef[],
    []
  );

  const gridOptions: GridOptions = useMemo(
    () => ({
      animateRows: true,
      rowGroupPanelShow: "never",
      sideBar: false,
      pagination: false,
      suppressMovableColumns: true,
      domLayout: "normal",
    }),
    []
  );

  const handleOnSelectionChange = useCallback(
    (event: MapboxDraw.DrawSelectionChangeEvent) => {
      if (event.features.length === 1) {
        dispatch(selectFeature(event.features[0]));
      }
    },
    [dispatch]
  );

  const handleOnRender = useCallback(() => {
    setRowData(drawControl.current.getAll().features.filter(({ properties }) => properties?.excluded === "true"));
  }, [drawControl]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelectionChange);
    map?.on("draw.prioritize", handleOnRender);

    return () => {
      map?.off("draw.selectionchange", handleOnSelectionChange);
      map?.off("draw.prioritize", handleOnRender);
    };
  }, [dispatch, handleOnRender, handleOnSelectionChange, map]);

  const handleOnConfirm = async () => {
    const payload = rowData.filter(({ id }) => typeof id === "string").map(({ id }) => ({ ID: id }));
    runWorkflow?.({ ExcludeFeatures: payload });
    handleBack();
  };

  /**
   * Callback function called when a row is double clicked
   * Feature is zoomed to & selected
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    drawControl.current.changeMode("simple_select", { featureIds: [event.data.properties.id] });
    dispatch(zoomTo([event.data]));
    dispatch(selectFeature(event.data));
  };

  const handleBack = () => {
    // For each of the features selected during the draw, restore the feature state isEditing = false
    drawControl.current.getAll().features.forEach(({ id }) => {
      map?.setFeatureState({ id, source: NBN_SOURCE, sourceLayer: NBN_SOURCE_LAYER }, { isEditing: false });
    });

    setDrawMode("no_feature");
    goBack();
  };

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Exclude service locations" />
      <Alert severity="info">
        <AlertTitle>Exclude service locations</AlertTitle>
        Choose the service locations you'd like to exclude from this scenario. When you're finished, click "Confirm" to proceed.
      </Alert>

      <Box sx={{ height: 300, width: "100%", mt: 2 }}>
        <AgGrid
          rowData={rowData}
          columnDefs={columns}
          gridOptions={gridOptions}
          variant="outlined"
          size="compact"
          onRowDoubleClicked={onRowDoubleClicked}
        />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleBack}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleOnConfirm}
          sx={{ ml: 1, px: 2 }}
          data-testid="finish-button"
          disabled={rowData.length === 0}
        >
          Confirm
        </Button>
      </Box>

      <DrawHandler
        startingMode="simple_select"
        initialFeatures={null}
        sources={sources}
        config={config}
        options={drawHandlerOptions}
        styles={nbnDrawStyles}
        modes={modes}
      />
    </>
  );
};

export default ExcludeDrawPanel;
